export type ProntuarioNotesSegment =
	| {
			type: 'plain';
			text: string;
			start: number;
			end: number;
	  }
	| {
			type: 'bold' | 'italic' | 'code';
			inner: string;
			open: string;
			close: string;
			start: number;
			end: number;
	  };

export const BULLET_PREFIX = '• ';

export const isCursorInRange = (
	cursor: number | null,
	start: number,
	end: number,
): boolean => cursor !== null && cursor >= start && cursor <= end;

/** Sem espaço colado nos delimitadores — ex.: `*texto*` sim, `* texto *` não. */
const hasTightMarkdownInner = (inner: string): boolean =>
	inner.length > 0 && !/^\s/.test(inner) && !/\s$/.test(inner);

const parseInlineSegments = (
	line: string,
	lineOffset: number,
): ProntuarioNotesSegment[] => {
	const segments: ProntuarioNotesSegment[] = [];
	let index = 0;

	while (index < line.length) {
		const absStart = lineOffset + index;
		const rest = line.slice(index);

		const boldMatch = /^\*\*(.+?)\*\*/.exec(rest);
		if (boldMatch && hasTightMarkdownInner(boldMatch[1])) {
			const raw = boldMatch[0];
			segments.push({
				type: 'bold',
				inner: boldMatch[1],
				open: '**',
				close: '**',
				start: absStart,
				end: absStart + raw.length,
			});
			index += raw.length;
			continue;
		}

		const codeMatch = /^`([^`]+)`/.exec(rest);
		if (codeMatch && hasTightMarkdownInner(codeMatch[1])) {
			const raw = codeMatch[0];
			segments.push({
				type: 'code',
				inner: codeMatch[1],
				open: '`',
				close: '`',
				start: absStart,
				end: absStart + raw.length,
			});
			index += raw.length;
			continue;
		}

		const italicMatch = /^\*([^*\n]+?)\*/.exec(rest);
		if (italicMatch && hasTightMarkdownInner(italicMatch[1])) {
			const raw = italicMatch[0];
			segments.push({
				type: 'italic',
				inner: italicMatch[1],
				open: '*',
				close: '*',
				start: absStart,
				end: absStart + raw.length,
			});
			index += raw.length;
			continue;
		}

		const nextSpecial = rest.search(/\*\*|`|\*/);
		if (nextSpecial === -1) {
			segments.push({
				type: 'plain',
				text: rest,
				start: absStart,
				end: lineOffset + line.length,
			});
			break;
		}

		if (nextSpecial > 0) {
			segments.push({
				type: 'plain',
				text: rest.slice(0, nextSpecial),
				start: absStart,
				end: absStart + nextSpecial,
			});
			index += nextSpecial;
			continue;
		}

		segments.push({
			type: 'plain',
			text: rest.charAt(0),
			start: absStart,
			end: absStart + 1,
		});
		index += 1;
	}

	return segments;
};

export const parseProntuarioNotesDocument = (markdown: string) => {
	const lines = markdown.split('\n');
	let offset = 0;

	return lines.map((line) => {
		const hasBullet = line.startsWith(BULLET_PREFIX);
		const body = hasBullet ? line.slice(BULLET_PREFIX.length) : line;
		const bodyOffset = hasBullet ? offset + BULLET_PREFIX.length : offset;

		const entry = {
			line,
			lineOffset: offset,
			bulletPrefix: hasBullet,
			segments: parseInlineSegments(body, bodyOffset),
		};

		offset += line.length + 1;
		return entry;
	});
};
